import { useEffect } from "react";

import { BackContext } from "@keepeek/commons";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";

import { backContextState } from "./atoms";
import { initBackContextState } from "./constants";

type UseBackContext = {
  goToPageWithBackContext: (backContext: BackContext) => Promise<void>;
  goBackToSearch: () => Promise<void>;
};

export const useBackContext = (): UseBackContext => {
  const [context, setContext] = useRecoilState(backContextState);
  const router = useRouter();

  useEffect(() => {
    if (router.pathname === context.sourcePage?.pathname) {
      // flush the context when we're back on the search page
      setContext(initBackContextState);
    }
    // Want it on page change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  const goToPageWithBackContext = async (backContext: BackContext) => {
    // set the context when we're leaving for a page
    setContext(backContext);
    await router.push({
      pathname: backContext.targetPage?.pathname,
      query: backContext.targetPage?.query,
      hash: backContext.targetPage?.hash,
    });
  };

  const goBackToSearch = async () => {
    // In case we have no sourcePage to go back
    // Fallback to router.back() just to be sure to provide feedback to the user
    if (!context.sourcePage) {
      router.back();
      return Promise.resolve();
    }
    // Take the context
    const { pathname, query, hash } = context.sourcePage;
    // navigate with context to the search page
    await router.push({ pathname, query, hash });
    // Flush the context
    setContext(initBackContextState);
  };

  return { goToPageWithBackContext, goBackToSearch };
};
